import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-expanded", "value", "data-testid", "aria-invalid", "title"]
const _hoisted_2 = ["aria-selected", "data-testid", "onClick"]
const _hoisted_3 = {
  key: 0,
  class: "px-3 py-2 text-xs text-neutral50 border-t border-lightGray30",
  "data-testid": "doctor-suggestion-overflow"
}
const _hoisted_4 = {
  key: 1,
  class: "px-3 py-2 text-xs text-neutral50",
  "data-testid": "doctor-suggestion-empty"
}

import { computed, ref, watch, onUnmounted, type CSSProperties } from 'vue'
import type {
  DSAttendanceDoctorSuggestion,
  DSAttendanceDoctorSuggestionField
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { useDoctorSuggestions } from '../composables/useDoctorSuggestions'
import { formatDoctorSuggestion } from '../utils/crm-format'

/**
 * RF-03/RF-04 — editor de célula de CRM ou de nome do médico na folha de monitoramento,
 * injetado pelos slots `cell-edit-*` do `AttendanceSheetCard`. A célula continua sendo o
 * mesmo `<input>` de texto livre de hoje (RN-04): a sugestão acelera a transcrição, e
 * selecioná-la tem efeito exclusivamente textual — nada vincula a linha ao cadastro.
 */
const CELL_ERROR_MESSAGE = 'Campo obrigatório: a linha está em uso e precisa ficar completa.'

const POPOVER_GAP_PX = 4
/** Respiro mínimo até a borda da viewport, para a lista nunca colar na quina. */
const POPOVER_VIEWPORT_MARGIN_PX = 8
/** Altura desejada: o teto de 8 sugestões da RF-03 mais o rodapé de excedente cabem nisto. */
const POPOVER_MAX_HEIGHT_PX = 320
/**
 * Piso da altura: com pouquíssimo espaço nas duas direções, é melhor a lista vazar a borda da
 * viewport (parcialmente visível, como era antes) do que colapsar em zero — trocar uma lista
 * invisível por outra seria reintroduzir justamente o defeito que este arquivo corrige.
 */
const POPOVER_MIN_HEIGHT_PX = 120

/** Posição do popover, recalculada enquanto a lista está aberta. */

export default /*#__PURE__*/_defineComponent({
  __name: 'DoctorSuggestionCell',
  props: {
    modelValue: {},
    containerId: {},
    field: {},
    columnKey: {},
    rowIndex: {},
    invalid: { type: Boolean },
    highlighted: { type: Boolean }
  },
  emits: ["update:modelValue", "select"],
  setup(__props: any, { emit }) {

const props = __props;





/** Mesma mensagem do editor padrão do `AttendanceSheetCard` (RF-02). */
const inputEl = ref<HTMLInputElement | null>(null)
/**
 * N8/#2359 — o texto exibido vive em estado local, não na prop.
 *
 * `:value="modelValue"` sozinho não funciona nesta célula: a #2111 a monta como editor
 * customizado do jspreadsheet via `createApp(Componente, props)`, e esse objeto de props é
 * **estático** — o `onUpdate:modelValue` do editor atualiza a closure dele, nunca a prop. Como
 * o Vue repatcha a prop `value` em **toda** re-renderização (é assim que ele mantém um input
 * controlado autoritativo), qualquer re-render devolvia o input ao valor de abertura: abrir a
 * lista de sugestões é uma re-renderização, então o que o auditor já tinha digitado era
 * apagado ali. A perda ficava proporcional à velocidade de digitação — o que entrou antes do
 * re-render morria, o que entrou depois sobrevivia (UAT-16/21/24: `99999-ZZ` chegando `ZZ`).
 *
 * O `watch` preserva o contrato de `v-model` para qualquer consumidor que alimente a prop de
 * volta de verdade; o `emit` de `handleInput` continua igual.
 */
const text = ref(props.modelValue)
watch(
  () => props.modelValue,
  (value) => {
    text.value = value
  }
)
const isFocused = ref(false)
const isDismissed = ref(false)
const activeIndex = ref(-1)

const { suggestions, overflowCount, hasResult, search, reset } = useDoctorSuggestions(
  props.field,
  () => props.containerId
)

/**
 * RN-05 — nunca há lista aberta previamente: ela só existe depois de uma busca concluída,
 * com o foco na célula e sem dispensa por `Esc` ou clique fora.
 */
const isListOpen = computed({
  get: () => isFocused.value && !isDismissed.value && hasResult.value,
  set: (open: boolean) => {
    if (!open) isDismissed.value = true
  }
})

/** Folga entre a célula e a lista. */
const popoverPosition = ref({
  left: 0,
  top: 0,
  bottom: 0,
  maxHeight: POPOVER_MAX_HEIGHT_PX,
  openUpwards: false
})

function updatePopoverPosition() {
  const input = inputEl.value
  // web#2362 — `getBoundingClientRect()` de elemento FORA do documento devolve um `DOMRect`
  // todo zerado, e o objeto é truthy: a guarda anterior (`if (!rect)`) não pegava esse caso e
  // a lista ia para a quina (0, 4) da viewport. Só há ancoragem válida com o input no
  // documento — a célula pode ter sido arrancada por um `destroyGrid()` no meio da busca.
  if (!input?.isConnected) return
  const rect = input.getBoundingClientRect()

  const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP_PX - POPOVER_VIEWPORT_MARGIN_PX
  const spaceAbove = rect.top - POPOVER_GAP_PX - POPOVER_VIEWPORT_MARGIN_PX
  // Abre para cima quando não cabe abaixo e sobra mais espaço acima: célula nas últimas
  // linhas da folha, onde a lista ancorada para baixo nasceria fora da tela.
  const openUpwards = spaceBelow < Math.min(POPOVER_MAX_HEIGHT_PX, spaceAbove)

  popoverPosition.value = {
    left: rect.left,
    top: rect.bottom + POPOVER_GAP_PX,
    // `bottom` (e não `top`) no caminho para cima: a lista encosta na célula qualquer que
    // seja a altura real do conteúdo, sem sobrar vão quando há menos de 8 sugestões.
    bottom: window.innerHeight - rect.top + POPOVER_GAP_PX,
    maxHeight: Math.max(
      Math.min(POPOVER_MAX_HEIGHT_PX, openUpwards ? spaceAbove : spaceBelow),
      POPOVER_MIN_HEIGHT_PX
    ),
    openUpwards
  }
}

const popoverStyle = computed<CSSProperties>(() => {
  const { left, top, bottom, maxHeight, openUpwards } = popoverPosition.value
  return {
    // `position` no estilo inline, não pela utility `fixed`: o popover é teleportado, e com
    // `important: '#app'` as utilities só valem dentro de `#app` (ver o comentário do
    // `Teleport`). Aqui a geometria vale onde quer que o popover seja montado.
    position: 'fixed',
    left: `${left}px`,
    ...(openUpwards ? { bottom: `${bottom}px` } : { top: `${top}px` }),
    minWidth: '260px',
    maxWidth: '360px',
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto'
  }
})

watch(isListOpen, (open) => {
  if (open) {
    updatePopoverPosition()
    window.addEventListener('scroll', updatePopoverPosition, true)
    window.addEventListener('resize', updatePopoverPosition)
  } else {
    window.removeEventListener('scroll', updatePopoverPosition, true)
    window.removeEventListener('resize', updatePopoverPosition)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', updatePopoverPosition, true)
  window.removeEventListener('resize', updatePopoverPosition)
})

/**
 * RN-05 — sair da célula descarta o resultado: voltar a ela nunca reabre a lista sem
 * digitação nova. O clique numa sugestão não passa por aqui, porque a lista bloqueia o
 * `mousedown` que causaria o blur.
 */
function handleBlur() {
  isFocused.value = false
  activeIndex.value = -1
  reset()
}

function handleInput(event: Event) {
  const value = (event.target as HTMLInputElement).value
  text.value = value
  emit('update:modelValue', value)
  isDismissed.value = false
  activeIndex.value = -1
  search(value)
}

/**
 * RF-04 — quem grava as células é a tela, que conhece as duas colunas da linha. Aqui a
 * lista apenas se fecha; o preenchimento programático não reabre a busca.
 */
function select(suggestion: DSAttendanceDoctorSuggestion) {
  emit('select', suggestion)
  activeIndex.value = -1
  reset()
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    // RF-10 (#2118) — o primeiro `Esc` fecha a lista mantendo o texto digitado
    // (`stopPropagation` some com o evento aqui). O segundo `Esc`, sem lista aberta, NÃO
    // é tratado por este componente — o `return` abaixo o deixa borbulhar até o
    // `keyDownControls` nativo do jspreadsheet no `document`, que cancela a edição
    // (`closeEditor(cell, false)`) chamando o `closeEditor` do editor customizado
    // (`doctor-suggestion-cell-editor.ts`), já responsável por restaurar
    // `instance.options.data[y][x]` no `cell.textContent`. Nenhum estado extra precisou
    // ser guardado aqui — verificado com teste de integração em
    // `tests/unit/doctor-suggestion-cell-editor.spec.ts`.
    if (!isListOpen.value) return
    event.stopPropagation()
    isDismissed.value = true
    activeIndex.value = -1
    return
  }

  if (event.key === 'Tab') {
    // Fecha a lista e comita o texto digitado — o valor já sobe a cada tecla.
    isDismissed.value = true
    return
  }

  if (!isListOpen.value || suggestions.value.length === 0) return

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    activeIndex.value = Math.min(activeIndex.value + 1, suggestions.value.length - 1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
    return
  }

  if (event.key === 'Enter' && activeIndex.value >= 0) {
    event.preventDefault()
    select(suggestions.value[activeIndex.value])
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("input", {
      ref_key: "inputEl",
      ref: inputEl,
      type: "text",
      role: "combobox",
      autocomplete: "off",
      "aria-autocomplete": "list",
      "aria-expanded": isListOpen.value,
      value: text.value,
      "data-testid": `sheet-cell-input-${_ctx.columnKey}-${_ctx.rowIndex}`,
      "aria-invalid": _ctx.invalid,
      title: _ctx.invalid ? CELL_ERROR_MESSAGE : undefined,
      class: _normalizeClass(["border rounded-md px-3 py-2 w-full text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#2F7F91] focus:border-transparent transition-all", [
      _ctx.invalid ? 'border-negative ring-1 ring-negative' : 'border-gray-400',
      _ctx.highlighted ? 'bg-primary5' : 'bg-white'
    ]]),
      onInput: handleInput,
      onKeydown: handleKeydown,
      onBlur: handleBlur,
      onFocus: _cache[0] || (_cache[0] = ($event: any) => (isFocused.value = true))
    }, null, 42, _hoisted_1),
    (_openBlock(), _createBlock(_Teleport, { to: "#app" }, [
      (isListOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            role: "listbox",
            class: "z-50 bg-white rounded-md shadow-lg py-1 normal-case text-left",
            style: _normalizeStyle(popoverStyle.value),
            "data-testid": "doctor-suggestion-list",
            onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop","prevent"]))
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(suggestions), (suggestion, index) => {
              return (_openBlock(), _createElementBlock("button", {
                key: suggestion.id,
                type: "button",
                role: "option",
                "aria-selected": index === activeIndex.value,
                "data-testid": `doctor-suggestion-option-${index}`,
                class: _normalizeClass(["w-full text-left px-3 py-2 text-sm text-textPrimary hover:bg-lightGray30", index === activeIndex.value ? 'bg-lightGray30' : '']),
                onClick: ($event: any) => (select(suggestion))
              }, _toDisplayString(_unref(formatDoctorSuggestion)(suggestion)), 11, _hoisted_2))
            }), 128)),
            (_unref(overflowCount) > 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_3, " + " + _toDisplayString(_unref(overflowCount)) + " resultados — continue digitando para refinar ", 1))
              : _createCommentVNode("", true),
            (_unref(suggestions).length === 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_4, " Nenhum profissional do cadastro corresponde ao que foi digitado. "))
              : _createCommentVNode("", true)
          ], 36))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})